export interface Case {
  id: string
  caseNumber: string
  internalNumber: string
  title: string
  description?: string
  clientId: string
  client?: {
    id: string
    name: string
    clientNumber: string
    clientType: string
    phone?: string
    email?: string
  }
  area: CaseArea
  status: CaseStatus
  priority: CasePriority
  processNumber?: string
  courtName?: string
  courtNumber?: string
  judgeName?: string
  estimatedValue?: number
  successFeePercentage?: number
  responsibleLawyerId?: string
  responsibleLawyerName?: string
  teamMembers: string[]
  startDate: string
  expectedEndDate?: string
  actualEndDate?: string
  tags: string[]
  isArchived: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  // Conversion tracking fields
  sourceType?: 'FROM_INTERACTION' | 'FROM_CASE' | 'DIRECT'
  sourceInteractionId?: string
  sourceCaseId?: string
  sourceInteractionNumber?: string
  sourceCaseNumber?: string
  isLegalProcess?: boolean
  convertedToLegalProcessId?: string
  convertedAt?: string
}

export enum CaseStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  FINISHED = 'FINISHED',
  CLOSED_WON = 'CLOSED_WON',
  CLOSED_LOST = 'CLOSED_LOST',
  CLOSED_SETTLED = 'CLOSED_SETTLED',
  ARCHIVED = 'ARCHIVED',
  CONVERTED_TO_LEGAL = 'CONVERTED_TO_LEGAL'
}

export enum CasePriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT'
}

export enum CaseArea {
  CIVIL = 'CIVIL',
  CRIMINAL = 'CRIMINAL',
  LABOR = 'LABOR',
  TAX = 'TAX',
  CORPORATE = 'CORPORATE',
  FAMILY = 'FAMILY',
  REAL_ESTATE = 'REAL_ESTATE',
  INTELLECTUAL_PROPERTY = 'INTELLECTUAL_PROPERTY',
  ENVIRONMENTAL = 'ENVIRONMENTAL',
  CONSUMER = 'CONSUMER',
  ADMINISTRATIVE = 'ADMINISTRATIVE',
  BANKRUPTCY = 'BANKRUPTCY'
}

export interface CaseParty {
  id: string
  caseId: string
  partyType: string
  name: string
  document?: string
  contactInfo: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/** Documento do caso como a API devolve (`GET /api/cases/:id/documents`). */
export interface CaseDocument {
  id: string
  entityId?: string
  /** Nome original do arquivo, como foi enviado (pela tela ou pelo Hermes). */
  fileName: string
  filePath?: string
  fileSize?: number
  mimeType?: string
  documentType: string
  /** Para que serve o arquivo; opcional. */
  description?: string | null
  metadata?: { isConfidential?: boolean } & Record<string, unknown>
  uploadedBy: string
  uploadedByName?: string
  uploadedAt: string
}

export interface CaseTimelineEvent {
  id: string
  caseId: string
  eventType: string
  title: string
  description?: string
  eventDate: string
  metadata: Record<string, unknown>
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateCaseDto {
  title: string
  description?: string
  clientId: string
  area: CaseArea
  priority?: CasePriority
  courtName?: string
  courtNumber?: string
  judgeName?: string
  estimatedValue?: number
  successFeePercentage?: number
  responsibleLawyerId?: string
  responsibleLawyerName?: string
  teamMembers?: string[]
  startDate: string
  expectedEndDate?: string
  tags?: string[]
}

export interface UpdateCaseDto {
  title?: string
  description?: string
  area?: CaseArea
  status?: CaseStatus
  priority?: CasePriority
  courtName?: string
  courtNumber?: string
  judgeName?: string
  estimatedValue?: number
  successFeePercentage?: number
  responsibleLawyerId?: string
  responsibleLawyerName?: string
  teamMembers?: string[]
  expectedEndDate?: string
  tags?: string[]
}

export interface CaseFilters {
  search?: string
  clientId?: string
  status?: CaseStatus[]
  area?: CaseArea[]
  priority?: CasePriority[]
  responsibleLawyerId?: string
  startDateFrom?: string
  startDateTo?: string
  expectedEndDateFrom?: string
  expectedEndDateTo?: string
  estimatedValueMin?: number
  estimatedValueMax?: number
  tags?: string[]
  hasCourtInfo?: boolean
  hasEstimatedValue?: boolean
  hasResponsibleLawyer?: boolean
  isLegalProcess?: boolean
  onlyLegalProcesses?: boolean
  excludeLegalProcesses?: boolean
}

export interface PaginationOptions {
  page: number
  limit: number
  sort?: string
}

export interface CaseListResponse {
  cases: Case[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface CaseStats {
  totalCases: number
  activeCases: number
  closedCases: number
  byStatus: Record<CaseStatus, number>
  byArea: Record<CaseArea, number>
  byPriority: Record<CasePriority, number>
  totalEstimatedValue: number
  averageEstimatedValue: number
  casesWithCourtInfo: number
  casesWithResponsibleLawyer: number
  recentCases: Case[]
  upcomingDeadlines: Case[]
}

export interface ChangeCaseStatusDto {
  status: CaseStatus
  reason?: string
}

export const CaseStatusLabels: Record<CaseStatus, string> = {
  [CaseStatus.DRAFT]: 'Rascunho',
  [CaseStatus.ACTIVE]: 'Em andamento',
  [CaseStatus.SUSPENDED]: 'Suspenso',
  [CaseStatus.FINISHED]: 'Finalizado',
  [CaseStatus.CLOSED_WON]: 'Procedente',
  [CaseStatus.CLOSED_LOST]: 'Improcedente',
  [CaseStatus.CLOSED_SETTLED]: 'Acordo',
  [CaseStatus.ARCHIVED]: 'Arquivado',
  [CaseStatus.CONVERTED_TO_LEGAL]: 'Convertido em processo'
}

/** Status oferecidos ao clicar na etiqueta (Rascunho e Convertido só aparecem em casos antigos). */
export const SELECTABLE_CASE_STATUSES: readonly CaseStatus[] = [
  CaseStatus.ACTIVE,
  CaseStatus.SUSPENDED,
  CaseStatus.CLOSED_WON,
  CaseStatus.CLOSED_LOST,
  CaseStatus.CLOSED_SETTLED,
  CaseStatus.FINISHED,
  CaseStatus.ARCHIVED,
]

export const CasePriorityLabels: Record<CasePriority, string> = {
  [CasePriority.LOW]: 'Baixa',
  [CasePriority.MEDIUM]: 'Média',
  [CasePriority.HIGH]: 'Alta',
  [CasePriority.URGENT]: 'Urgente'
}

export const CaseAreaLabels: Record<CaseArea, string> = {
  [CaseArea.CIVIL]: 'Cível',
  [CaseArea.CRIMINAL]: 'Criminal',
  [CaseArea.LABOR]: 'Trabalhista',
  [CaseArea.TAX]: 'Tributário',
  [CaseArea.CORPORATE]: 'Empresarial',
  [CaseArea.FAMILY]: 'Família',
  [CaseArea.REAL_ESTATE]: 'Imobiliário',
  [CaseArea.INTELLECTUAL_PROPERTY]: 'Propriedade Intelectual',
  [CaseArea.ENVIRONMENTAL]: 'Ambiental',
  [CaseArea.CONSUMER]: 'Consumidor',
  [CaseArea.ADMINISTRATIVE]: 'Administrativo',
  [CaseArea.BANKRUPTCY]: 'Falência e Recuperação'
}